/**
 * Legal — the legal-recovery workbench (#25, Josh 09/28).
 *
 * Tabs:
 *   Overview           every Default deal, in two sections:
 *                        Lawyer Collections — active legal case, grouped by law firm (money once a month)
 *                        In-House           — no lawyer (money can arrive daily / weekly)
 *                      Row actions post through the SAME per-deal endpoints the Default tab uses:
 *                        recovery status (Died → comment → "write off?" prompt), proposed settlement,
 *                        add collection, write off. Nothing new touches the ledger.
 *                      "Export status request" → CSV for the lawyer; "Import lawyer replies" reads it
 *                      back and applies statuses in one pass (/legal/bulk-status).
 *   Remittance         manual lawyer remittance entry (default, Madeb) — CSV wizard behind a toggle
 *   Statements         syndicator legal statements (#23/#24 — next build)
 *
 * Internal-only: the lawyer works off monthly reports, no login.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import LawyerRemittanceImport from './LawyerRemittanceImport';
import StatementsTab from '../components/legal/StatementsTab';
import RemittanceEntry from '../components/legal/RemittanceEntry';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Scale, Building2, Download, Upload, RefreshCw, ChevronDown, ChevronRight, ExternalLink, Plus, Ban, Loader2, Search } from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => new Date().toISOString().slice(0, 10);

// ─────────────────────────────────────────────────────────────────────────────
const Legal = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'overview';
  const setTab = (t) => { params.set('tab', t); setParams(params, { replace: true }); };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dormantOnly, setDormantOnly] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [csvMode, setCsvMode] = useState(false);   // Josh 09/29: manual entry is the default; CSV importer kept as fallback

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/legal/overview`, { headers: getAuthHeaders() });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load the Legal overview');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const rowVisible = useCallback((r) => {
    if (dormantOnly && !r.dormant) return false;
    if (statusFilter !== 'all' && r.recovery_status !== statusFilter) return false;
    if (q) {
      const s = q.toLowerCase();
      if (!(`${r.deal_id} ${r.deal_name} ${r.law_firm || ''}`.toLowerCase().includes(s))) return false;
    }
    return true;
  }, [q, statusFilter, dormantOnly]);

  const firms = useMemo(() => (data?.lawyer_groups || []).map(g => g.law_firm), [data]);
  const filtering = Boolean(q || statusFilter !== 'all' || dormantOnly);

  // Monthly reminder (Josh 09/29): the lawyer remits ~7th; until this month's remittance is
  // uploaded, a popup asks for it. Dismissed once per month (localStorage), reappears next month.
  const [reminder, setReminder] = useState(null);
  const [reminderOpen, setReminderOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/legal/remittance-reminder`, { headers: getAuthHeaders() }).then(r => {
      setReminder(r.data);
      let dismissed = null;
      try { dismissed = localStorage.getItem('legal.remittanceReminderDismissed'); } catch (e) { /* ignore */ }
      if (r.data.due && dismissed !== r.data.month) setReminderOpen(true);
    }).catch(() => {});
  }, [getAuthHeaders, loading]);
  const dismissReminder = () => {
    try { localStorage.setItem('legal.remittanceReminderDismissed', reminder?.month || ''); } catch (e) { /* ignore */ }
    setReminderOpen(false);
  };

  return (
    <Layout>
      <div className="space-y-6 p-6 max-w-[1500px] mx-auto" data-testid="legal-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Scale className="h-6 w-6 text-indigo-600" /> Legal
            </h1>
            <p className="text-sm text-slate-500 mt-1">Every defaulted deal — lawyer collections and in-house — with the actions you take on them.</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="overview" data-testid="legal-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="remittance" data-testid="legal-tab-remittance">Remittance</TabsTrigger>
            <TabsTrigger value="statements" data-testid="legal-tab-statements">Statements</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-5 mt-4">
            {reminder?.due && (
              <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-amber-300 bg-amber-50 text-sm" data-testid="remittance-banner">
                <span><b>Lawyer remittance for {reminder.month} not entered yet.</b> Enter it under Remittance, then build the syndicator statements.</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setTab('remittance')}>Go to Remittance</Button>
                </div>
              </div>
            )}
            {data && <Totals t={data.totals} dormantDays={data.dormant_days} />}
            <Toolbar q={q} setQ={setQ} statusFilter={statusFilter} setStatusFilter={setStatusFilter}
                     dormantOnly={dormantOnly} setDormantOnly={setDormantOnly}
                     statusOptions={data?.recovery_status_options || []} firms={firms}
                     getAuthHeaders={getAuthHeaders} onApplied={load} />

            {loading && !data && <p className="text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-1" />Loading…</p>}

            {data && (
              <>
                <SectionHeader icon={Scale} title="Lawyer Collections" subtitle="Active legal case — the lawyer remits once a month" totals={data.totals.lawyer} />
                {data.lawyer_groups.length === 0 && <p className="text-sm text-muted-foreground pl-1">No deals with a lawyer.</p>}
                {data.lawyer_groups.map(g => {
                  const rows = g.deals.filter(rowVisible);
                  if (!rows.length && filtering) return null;
                  const open = !collapsed[g.law_firm];
                  return (
                    <Card key={g.law_firm} data-testid={`legal-firm-${g.law_firm}`}>
                      <CardHeader className="py-3 cursor-pointer" onClick={() => setCollapsed(c => ({ ...c, [g.law_firm]: open }))}>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <CardTitle className="text-base flex items-center gap-2">
                            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            {g.law_firm}
                            {g.contingency_rate != null && <Badge variant="outline">{g.contingency_rate}% contingency</Badge>}
                            <span className="text-xs text-muted-foreground font-normal">{g.totals.deals} deal{g.totals.deals === 1 ? '' : 's'}</span>
                          </CardTitle>
                          <GroupTotals t={g.totals} />
                        </div>
                      </CardHeader>
                      {open && <CardContent className="pt-0"><DealTable rows={rows} getAuthHeaders={getAuthHeaders} statusOptions={data.recovery_status_options} onChanged={load} navigate={navigate} /></CardContent>}
                    </Card>
                  );
                })}

                <SectionHeader icon={Building2} title="In-House" subtitle="No lawyer — collected directly, daily or weekly" totals={data.totals.in_house} />
                <Card>
                  <CardContent className="pt-4">
                    <DealTable rows={data.in_house.filter(rowVisible)} getAuthHeaders={getAuthHeaders} statusOptions={data.recovery_status_options} onChanged={load} navigate={navigate} inHouse />
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          <TabsContent value="remittance" className="mt-4 space-y-3">
            <div className="flex justify-end">
              <Button size="sm" variant="ghost" onClick={() => setCsvMode(v => !v)} data-testid="remittance-mode-toggle">
                {csvMode ? '← Enter by hand' : 'Have a CSV from the lawyer? Use the importer'}
              </Button>
            </div>
            {csvMode ? <LawyerRemittanceImport embedded /> : <RemittanceEntry getAuthHeaders={getAuthHeaders} />}
          </TabsContent>

          <TabsContent value="statements" className="mt-4">
            <StatementsTab getAuthHeaders={getAuthHeaders} />
          </TabsContent>
        </Tabs>

        {/* monthly reminder popup */}
        <Dialog open={reminderOpen} onOpenChange={(v) => !v && dismissReminder()}>
          <DialogContent>
            <DialogHeader><DialogTitle>Lawyer remittance due — {reminder?.month}</DialogTitle></DialogHeader>
            <p className="text-sm">No lawyer collections have been posted this month. The lawyer usually remits around the {reminder?.remind_day}th.</p>
            <ol className="text-sm list-decimal pl-5 space-y-1 text-muted-foreground">
              <li>Enter the lawyer's remittance under <b>Remittance</b> (deal by deal, plus any invoices).</li>
              <li>Then build last month's syndicator statements under <b>Statements → Generate drafts</b>.</li>
            </ol>
            <DialogFooter>
              <Button variant="outline" onClick={dismissReminder}>Remind me next month</Button>
              <Button onClick={() => { setReminderOpen(false); setTab('remittance'); }}>Enter it now</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

// ─── header bits ─────────────────────────────────────────────────────────────
const Totals = ({ t, dormantDays }) => (
  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
    <Stat label="Default deals" value={t.all.deals} sub={`${t.lawyer.deals} lawyer · ${t.in_house.deals} in-house`} />
    <Stat label="Effective balance" value={formatCurrency(t.all.balance_with_fees)} />
    <Stat label="Gross recovered" value={formatCurrency(t.all.gross_collected)} sub={`${formatCurrency(t.all.lawyer_collected)} lawyer · ${formatCurrency(t.all.in_house_collected)} in-house`} color="text-green-600" />
    <Stat label="Net after lawyer fees" value={formatCurrency(t.all.net_recovered)} sub={`fees ${formatCurrency(t.all.lawyer_fees)}`} />
    <Stat label={`Dormant (${dormantDays}d+)`} value={t.all.dormant} sub={`${t.all.settled} settled · ${t.all.died} died · ${t.all.closed ?? 0} closed`} color={t.all.dormant ? 'text-amber-600' : ''} />
  </div>
);

const Stat = ({ label, value, sub, color = '' }) => (
  <Card><CardContent className="p-3">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-lg font-semibold ${color}`}>{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
  </CardContent></Card>
);

const SectionHeader = ({ icon: Icon, title, subtitle, totals }) => (
  <div className="flex items-end justify-between flex-wrap gap-2 pt-2">
    <div>
      <h2 className="text-lg font-semibold flex items-center gap-2"><Icon className="h-5 w-5 text-indigo-600" />{title}</h2>
      <p className="text-xs text-muted-foreground">{subtitle}</p>
    </div>
    <GroupTotals t={totals} />
  </div>
);

const GroupTotals = ({ t }) => (
  <div className="flex gap-4 text-xs text-muted-foreground">
    <span>Balance <b className="text-foreground">{formatCurrency(t.balance_with_fees)}</b></span>
    <span>Recovered <b className="text-green-600">{formatCurrency(t.gross_collected)}</b></span>
    <span>Net <b className="text-foreground">{formatCurrency(t.net_recovered)}</b></span>
    {t.dormant > 0 && <span className="text-amber-600">{t.dormant} dormant</span>}
  </div>
);

// ─── toolbar: search / filters / export / import ─────────────────────────────
const Toolbar = ({ q, setQ, statusFilter, setStatusFilter, dormantOnly, setDormantOnly, statusOptions, firms, getAuthHeaders, onApplied }) => {
  const [exportScope, setExportScope] = useState('lawyer');
  const [exportFirm, setExportFirm] = useState('__all__');
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const p = new URLSearchParams({ scope: exportScope });
      if (exportFirm !== '__all__') p.set('law_firm', exportFirm);
      const r = await axios.get(`${API}/legal/status-request.csv?${p}`, { headers: getAuthHeaders(), responseType: 'blob' });
      const cd = r.headers['content-disposition'] || '';
      const name = (cd.match(/filename="?([^"]+)"?/) || [])[1] || 'status_request.csv';
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex flex-wrap items-end gap-3 p-3 border rounded-lg bg-muted/30">
      <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input className="pl-8 w-56" placeholder="Deal #, merchant, firm…" value={q} onChange={e => setQ(e.target.value)} data-testid="legal-search" />
      </div>
      <div>
        <Label className="text-[11px] text-muted-foreground">Status</Label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[190px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {statusOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Button variant={dormantOnly ? 'default' : 'outline'} size="sm" onClick={() => setDormantOnly(v => !v)} data-testid="legal-dormant-toggle">
        Dormant only
      </Button>
      <div className="flex-1" />
      <div className="flex items-end gap-2 border-l pl-3">
        <div>
          <Label className="text-[11px] text-muted-foreground">Status request for</Label>
          <div className="flex gap-2">
            <Select value={exportScope} onValueChange={setExportScope}>
              <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="lawyer">Lawyer deals</SelectItem>
                <SelectItem value="in_house">In-house</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            {exportScope === 'lawyer' && firms.length > 1 && (
              <Select value={exportFirm} onValueChange={setExportFirm}>
                <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Every firm</SelectItem>
                  {firms.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={exporting} data-testid="legal-export-btn">
          <Download className="h-4 w-4 mr-1" /> Export CSV
        </Button>
        <Button size="sm" onClick={() => setImportOpen(true)} data-testid="legal-import-btn">
          <Upload className="h-4 w-4 mr-1" /> Import lawyer replies
        </Button>
      </div>
      <ImportRepliesDialog open={importOpen} onOpenChange={setImportOpen} getAuthHeaders={getAuthHeaders} onApplied={onApplied} />
    </div>
  );
};

// ─── import the lawyer's filled-in CSV → review → bulk apply ─────────────────
const ImportRepliesDialog = ({ open, onOpenChange, getAuthHeaders, onApplied }) => {
  const [rows, setRows] = useState(null);
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const reset = () => { setRows(null); if (fileRef.current) fileRef.current.value = ''; };

  const parse = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/legal/status-request/parse`, fd, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
      setOptions(r.data.recovery_status_options || []);
      setRows(r.data.rows.map(x => ({ ...x, status: x.suggested_status || '', comment: x.lawyer_notes || '', apply: !!x.suggested_status && x.found })));
      if (r.data.unmatched) toast.warning(`${r.data.unmatched} row(s) don't match a Default deal`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read that CSV');
    } finally {
      setBusy(false);
    }
  };

  const applyAll = async () => {
    const updates = rows.filter(r => r.apply && r.found && r.status).map(r => ({ deal_id: r.deal_uuid, status: r.status, comment: r.comment }));
    if (!updates.length) { toast.error('Nothing selected to apply'); return; }
    const died = updates.filter(u => u.status === 'Died in Settlement' && !u.comment.trim());
    if (died.length) { toast.error(`${died.length} "Died in Settlement" row(s) need a comment`); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/legal/bulk-status`, { updates }, { headers: getAuthHeaders() });
      toast[r.data.failed.length ? 'warning' : 'success'](`${r.data.updated} updated, ${r.data.unchanged} unchanged${r.data.failed.length ? `, ${r.data.failed.length} failed` : ''}`);
      if (r.data.failed.length) console.warn('bulk-status failures', r.data.failed);
      onOpenChange(false); reset(); onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk update failed');
    } finally {
      setBusy(false);
    }
  };

  const set = (i, patch) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-4xl">
        <DialogHeader><DialogTitle>Import lawyer replies</DialogTitle></DialogHeader>
        {!rows && (
          <div className="space-y-2 text-sm">
            <p className="text-muted-foreground">Upload the status-request CSV the lawyer filled in (the two right-hand columns). You review every row before anything is applied; statuses are only pre-selected when the lawyer's wording is unambiguous.</p>
            <Input ref={fileRef} type="file" accept=".csv,text/csv" onChange={e => parse(e.target.files?.[0])} disabled={busy} data-testid="legal-import-file" />
          </div>
        )}
        {rows && (
          <div className="max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-8"></TableHead><TableHead>Deal</TableHead><TableHead>Lawyer wrote</TableHead>
                <TableHead>Now</TableHead><TableHead>Set to</TableHead><TableHead>Comment</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i} className={!r.found ? 'opacity-50' : ''}>
                    <TableCell><input type="checkbox" checked={r.apply} disabled={!r.found || !r.status} onChange={e => set(i, { apply: e.target.checked })} /></TableCell>
                    <TableCell className="text-xs">#{r.deal_id}<br /><span className="text-muted-foreground">{r.found ? r.deal_name : 'not a Default deal'}</span></TableCell>
                    <TableCell className="text-xs max-w-[220px]"><div>{r.lawyer_status || <span className="text-muted-foreground">—</span>}</div>{r.lawyer_notes && <div className="text-muted-foreground">{r.lawyer_notes}</div>}</TableCell>
                    <TableCell className="text-xs">{r.current_status || '—'}</TableCell>
                    <TableCell>
                      <Select value={r.status || '__none__'} onValueChange={v => set(i, { status: v === '__none__' ? '' : v, apply: v !== '__none__' && r.found })} disabled={!r.found}>
                        <SelectTrigger className="w-[170px] h-8 text-xs"><SelectValue placeholder="pick" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— skip —</SelectItem>
                          {options.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell><Input className="h-8 text-xs" value={r.comment} onChange={e => set(i, { comment: e.target.value })} placeholder={r.status === 'Died in Settlement' ? 'required' : ''} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <DialogFooter>
          {rows && <Button variant="outline" onClick={reset} disabled={busy}>Choose another file</Button>}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          {rows && <Button onClick={applyAll} disabled={busy} data-testid="legal-import-apply">{busy ? 'Applying…' : `Apply ${rows.filter(r => r.apply && r.found && r.status).length} change(s)`}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── the deal table + row actions ────────────────────────────────────────────
const DealTable = ({ rows, getAuthHeaders, statusOptions, onChanged, navigate, inHouse = false }) => {
  const [dlg, setDlg] = useState(null);   // {kind: 'died'|'writeoff'|'settle'|'collect', row}
  if (!rows.length) return <p className="text-sm text-muted-foreground py-2">No deals match.</p>;

  const post = async (row, path, body, okMsg) => {
    try {
      const r = await axios.post(`${API}/default-recovery/${row.deal_uuid}/${path}`, body, { headers: getAuthHeaders() });
      toast.success(okMsg || r.data?.message || 'Saved');
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request failed');
      return null;
    }
  };

  const onStatus = async (row, status) => {
    if (status === row.recovery_status) return;
    if (status === 'Died in Settlement') { setDlg({ kind: 'died', row }); return; }
    if (status === 'Settled') { setDlg({ kind: 'settle', row }); return; }
    if (status === 'Closed') { setDlg({ kind: 'closed', row }); return; }
    if (await post(row, 'recovery-status', { status, comment: '' }, `#${row.deal_id}: ${status}`)) onChanged();
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow className="text-xs">
            <TableHead>Deal</TableHead>
            <TableHead className="text-right">Eff. balance</TableHead>
            <TableHead>Recovery status</TableHead>
            <TableHead>Settlement</TableHead>
            <TableHead className="text-right">Recovered</TableHead>
            <TableHead>Last money in</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(r => (
            <TableRow key={r.deal_uuid} className={r.dormant ? 'bg-amber-50/40' : ''} data-testid={`legal-row-${r.deal_id}`}>
              <TableCell className="align-top">
                <button className="font-medium hover:underline text-left" onClick={() => navigate(`/deals/${r.deal_uuid}`)}>#{r.deal_id}</button>
                <div className="text-xs text-muted-foreground max-w-[200px] truncate" title={r.deal_name}>{r.deal_name}</div>
                {r.defaulted_at && <div className="text-[10px] text-muted-foreground">default {formatDate(r.defaulted_at)}</div>}
              </TableCell>
              <TableCell className="text-right align-top font-semibold">{formatCurrency(r.balance_with_fees)}</TableCell>
              <TableCell className="align-top">
                <Select value={r.recovery_status} onValueChange={(v) => onStatus(r, v)}>
                  <SelectTrigger className="h-8 w-[180px] text-xs" data-testid={`legal-status-${r.deal_id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>{statusOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                {r.recovery_status_is_default && <div className="text-[10px] text-muted-foreground mt-0.5">not set yet (default)</div>}
                {r.recovery_status === 'Died in Settlement' && r.recovery_status_comment && <div className="text-[10px] text-red-700 mt-0.5 max-w-[180px]">{r.recovery_status_comment}</div>}
                {r.recovery_status === 'Closed' && r.recovery_status_comment && <div className="text-[10px] text-slate-300 mt-0.5 max-w-[180px] whitespace-pre-wrap" title={r.recovery_status_comment}>{r.recovery_status_comment}</div>}
              </TableCell>
              <TableCell className="align-top text-xs">
                {r.proposed_settlement ? (
                  <>
                    <div className="font-medium">{formatCurrency(r.proposed_settlement.amount)} <span className="text-muted-foreground">on {formatDate(r.proposed_settlement.date)}</span></div>
                    {r.settlement_progress && (
                      <div className={r.settlement_progress.complete ? 'text-emerald-700' : 'text-muted-foreground'}>
                        {r.settlement_progress.complete ? 'complete' : `${formatCurrency(r.settlement_progress.collected_since_agreement)} · ${r.settlement_progress.percent_complete ?? 0}%`}
                      </div>
                    )}
                    <button className="text-indigo-600 hover:underline" onClick={() => setDlg({ kind: 'settle', row: r })}>revise</button>
                  </>
                ) : (
                  <button className="text-indigo-600 hover:underline" onClick={() => setDlg({ kind: 'settle', row: r })}>record</button>
                )}
              </TableCell>
              <TableCell className="text-right align-top text-xs">
                <div className="font-medium text-green-600">{formatCurrency(r.gross_collected)}</div>
                {!inHouse && <div className="text-muted-foreground">lawyer {formatCurrency(r.lawyer_collected)}</div>}
                {r.in_house_collected > 0 && <div className="text-muted-foreground">in-house {formatCurrency(r.in_house_collected)}</div>}
                {r.lawyer_fees > 0 && <div className="text-muted-foreground">fees {formatCurrency(r.lawyer_fees)}</div>}
                {r.cutoff_missing && <div className="text-amber-700">no default date</div>}
              </TableCell>
              <TableCell className="align-top text-xs">
                {r.last_money_in ? formatDate(r.last_money_in) : <span className="text-muted-foreground">never</span>}
                {r.days_quiet != null && <div className={r.dormant ? 'text-amber-700 font-medium' : 'text-muted-foreground'}>{r.days_quiet}d quiet{r.dormant ? ' · dormant' : ''}</div>}
              </TableCell>
              <TableCell className="align-top">
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setDlg({ kind: 'collect', row: r })} title="Add collection"><Plus className="h-3 w-3 mr-1" />Collection</Button>
                  {r.write_off_eligible && <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-700" onClick={() => setDlg({ kind: 'writeoff', row: r })} title="Write off remaining balance"><Ban className="h-3 w-3 mr-1" />Write off</Button>}
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => navigate(`/deals/${r.deal_uuid}`)} title="Open deal"><ExternalLink className="h-3 w-3" /></Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {dlg?.kind === 'closed' && <ClosedDialog row={dlg.row} post={post} onClose={() => setDlg(null)} onChanged={onChanged} />}
      {dlg?.kind === 'died' && <DiedDialog row={dlg.row} post={post} onClose={() => setDlg(null)} onChanged={onChanged} onWriteOff={(row) => setDlg({ kind: 'writeoff', row })} />}
      {dlg?.kind === 'writeoff' && <WriteOffDialog row={dlg.row} post={post} onClose={() => setDlg(null)} onChanged={onChanged} />}
      {dlg?.kind === 'settle' && <SettlementDialog row={dlg.row} post={post} onClose={() => setDlg(null)} onChanged={onChanged} />}
      {dlg?.kind === 'collect' && <CollectionDialog row={dlg.row} post={post} onClose={() => setDlg(null)} onChanged={onChanged} />}
    </>
  );
};

// ─── dialogs (same endpoints as the deal's Default tab) ──────────────────────
// Closed (Josh 10/12): the file is closed out; optional note kept on the deal + history.
const ClosedDialog = ({ row, post, onClose, onChanged }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    const data = await post(row, 'recovery-status', { status: 'Closed', comment: note }, `#${row.deal_id}: Closed`);
    setBusy(false);
    if (!data) return;
    onChanged();
    onClose();
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>#{row.deal_id} — Close out this recovery file</DialogTitle></DialogHeader>
        <Label className="text-xs">Note (optional) — why / how it closed</Label>
        <Textarea rows={4} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. settled for less than the default calculation — merchant offered a lump sum…" />
        <p className="text-[11px] text-muted-foreground">Saved on the deal and in the recovery history. Posts nothing to the ledger; the deal status does not change.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={confirm} disabled={busy}>Mark Closed</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DiedDialog = ({ row, post, onClose, onChanged, onWriteOff }) => {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    const data = await post(row, 'recovery-status', { status: 'Died in Settlement', comment }, `#${row.deal_id}: Died in Settlement`);
    setBusy(false);
    if (!data) return;
    onChanged();
    if (data.write_off_eligible) onWriteOff({ ...row, balance_with_fees: data.effective_balance ?? row.balance_with_fees, died_comment: comment });
    else onClose();
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>#{row.deal_id} — Died in Settlement</DialogTitle></DialogHeader>
        <Label className="text-xs">Why did it die? (required)</Label>
        <Textarea rows={3} value={comment} onChange={e => setComment(e.target.value)} placeholder="e.g. bankruptcy filed, merchant never answered…" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={confirm} disabled={busy || !comment.trim()}>Mark Died in Settlement</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const WriteOffDialog = ({ row, post, onClose, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    const notes = row.died_comment ? `Died in Settlement — ${row.died_comment}` : '';
    const data = await post(row, 'write-off', { notes });
    setBusy(false);
    if (data) { onChanged(); onClose(); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Write off the remaining balance?</DialogTitle></DialogHeader>
        <p className="text-sm">#{row.deal_id} {row.deal_name} — remaining effective balance <b>{formatCurrency(row.balance_with_fees)}</b>.</p>
        <p className="text-xs text-muted-foreground">Posts a WRITE_OFF for that amount, closes the deal as Paid in Full / Settled (locked) and reports it as a write-off on the Legal Report. "Not now" leaves it in Default.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Not now</Button>
          <Button variant="destructive" onClick={confirm} disabled={busy}>{busy ? 'Posting…' : `Yes, write off ${formatCurrency(row.balance_with_fees)}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SettlementDialog = ({ row, post, onClose, onChanged }) => {
  const ps = row.proposed_settlement || {};
  const [form, setForm] = useState({ amount: ps.amount != null ? String(ps.amount) : '', terms: ps.terms || '', date: ps.date || today() });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast.error('Enter the agreed amount'); return; }
    setBusy(true);
    const data = await post(row, 'proposed-settlement', { amount, terms: form.terms, date: form.date }, `#${row.deal_id}: settlement ${formatCurrency(amount)} saved`);
    setBusy(false);
    if (data) { onChanged(); onClose(); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>#{row.deal_id} — Proposed settlement</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Current effective balance {formatCurrency(row.balance_with_fees)}. Saving marks the deal Settled; nothing is posted to the ledger.</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Agreed amount ($)</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></div>
          <div><Label className="text-xs">Agreement date</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
        </div>
        <div><Label className="text-xs">Terms</Label><Textarea rows={3} value={form.terms} onChange={e => setForm(f => ({ ...f, terms: e.target.value }))} placeholder="e.g. $500 weekly starting 10/01 until paid" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save & mark Settled'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CollectionDialog = ({ row, post, onClose, onChanged }) => {
  const lawyer = row.recovery_mode === 'via_lawyer';
  // Legacy deals: lawyer money in the ledger but no legal_cases row. The collections
  // endpoint branches on the CASE, so posting here would land as an in-house PAYMENT.
  if (lawyer && !row.has_legal_case) {
    return (
      <Dialog open onOpenChange={(v) => !v && onClose()}>
        <DialogContent>
          <DialogHeader><DialogTitle>#{row.deal_id} — set up the legal case first</DialogTitle></DialogHeader>
          <p className="text-sm">This deal has lawyer collections on its ledger but no legal case record (it predates the workflow). Until the case is set up on the deal's Default tab (lawyer + contingency %), a collection entered here would post as an in-house payment with no lawyer fee.</p>
          <p className="text-xs text-muted-foreground">Monthly remittances entered under the Remittance tab create the case automatically.</p>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button onClick={() => { window.location.assign(`/deals/${row.deal_uuid}`); }}>Open deal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  return <CollectionForm row={row} lawyer={lawyer} post={post} onClose={onClose} onChanged={onChanged} />;
};

const CollectionForm = ({ row, lawyer, post, onClose, onChanged }) => {
  const [form, setForm] = useState({ date: today(), gross: '', feePct: row.contingency_rate != null ? String(row.contingency_rate) : '', ref: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const gross = parseFloat(form.gross) || 0;
  const feePct = parseFloat(form.feePct) || 0;
  const fee = lawyer ? Math.round(gross * feePct) / 100 : 0;
  const save = async () => {
    if (!gross || gross <= 0) { toast.error('Enter the amount collected'); return; }
    setBusy(true);
    const body = { date: form.date, gross_collected: gross, remit_reference: form.ref, notes: form.notes };
    if (lawyer) body.lawyer_fee = fee;
    const data = await post(row, 'collections', body);
    setBusy(false);
    if (data) { onChanged(); onClose(); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>#{row.deal_id} — {lawyer ? 'Lawyer collection' : 'In-house payment'}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">
          {lawyer ? `Posts LAWYER_COLLECTION (gross) + LAWYER_FEE${row.law_firm ? ` for ${row.law_firm}` : ''}. ` : 'Posts a PAYMENT on the deal — same as an ACH payment. '}
          Effective balance now {formatCurrency(row.balance_with_fees)}.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
          <div><Label className="text-xs">{lawyer ? 'Gross collected ($)' : 'Amount ($)'}</Label><Input type="number" step="0.01" value={form.gross} onChange={e => setForm(f => ({ ...f, gross: e.target.value }))} autoFocus /></div>
          {lawyer && (
            <div><Label className="text-xs">Lawyer fee %</Label><Input type="number" step="0.01" value={form.feePct} onChange={e => setForm(f => ({ ...f, feePct: e.target.value }))} />
              <p className="text-[10px] text-muted-foreground mt-0.5">fee {formatCurrency(fee)} · net {formatCurrency(gross - fee)}</p></div>
          )}
          <div><Label className="text-xs">Reference</Label><Input value={form.ref} onChange={e => setForm(f => ({ ...f, ref: e.target.value }))} placeholder="remit / check #" /></div>
          <div className="col-span-2"><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy}>{busy ? 'Posting…' : 'Post collection'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Legal;
